/**
 * The home profile (docs/HOME.md "Profile"): asked once, one question per screen. It decides which chores, supplies
 * and storage rules apply (a glass-cooktop cleaner only for glass stoves, RO filters only with an RO system, tighter
 * chemical and medicine storage when children live there).
 */
export type StoveType = "induction_glass" | "glass_ceramic" | "gas_range" | "electric_coil";
export type KitchenSize = "small" | "medium" | "large";
export type FloorType = "hardwood" | "tile" | "vinyl" | "carpet";
export type HomeCabinet = "upper" | "lower" | "pantry" | "under_sink" | "bathroom" | "hall_closet" | "garage_basement";
export type HomeAppliance =
  | "espresso_machine" | "drip_coffee_maker" | "kettle" | "toaster" | "air_fryer" | "microwave" | "blender" | "stand_mixer"
  | "dishwasher" | "washer" | "dryer" | "fridge_water_filter" | "pitcher_filter" | "under_sink_ro";

export const STOVE_TYPES: readonly StoveType[] = ["induction_glass", "glass_ceramic", "gas_range", "electric_coil"];
export const KITCHEN_SIZES: readonly KitchenSize[] = ["small", "medium", "large"];
export const FLOOR_TYPES: readonly FloorType[] = ["hardwood", "tile", "vinyl", "carpet"];
export const HOME_CABINETS: readonly HomeCabinet[] = ["upper", "lower", "pantry", "under_sink", "bathroom", "hall_closet", "garage_basement"];
export const HOME_APPLIANCES: readonly HomeAppliance[] = [
  "espresso_machine", "drip_coffee_maker", "kettle", "toaster", "air_fryer", "microwave", "blender", "stand_mixer",
  "dishwasher", "washer", "dryer", "fridge_water_filter", "pitcher_filter", "under_sink_ro",
];

export const STOVE_LABEL: Record<StoveType, string> = {
  induction_glass: "Induction (glass top)", glass_ceramic: "Glass-ceramic (radiant)", gas_range: "Gas range", electric_coil: "Electric coil",
};
export const APPLIANCE_LABEL: Record<HomeAppliance, string> = {
  espresso_machine: "Espresso machine", drip_coffee_maker: "Drip coffee maker", kettle: "Electric kettle", toaster: "Toaster", air_fryer: "Air fryer",
  microwave: "Microwave", blender: "Blender", stand_mixer: "Stand mixer", dishwasher: "Dishwasher", washer: "Washing machine", dryer: "Dryer",
  fridge_water_filter: "Fridge water filter", pitcher_filter: "Pitcher water filter", under_sink_ro: "Under-sink reverse osmosis (RO)",
};

export interface HomeProfile {
  bedrooms: number;
  bathrooms: { full: number; half: number };
  kitchenSize: KitchenSize;
  cabinets: HomeCabinet[];
  stove: StoveType;
  appliances: HomeAppliance[];
  floors: FloorType[];
  household: { adults: number; kids: number; pets: number };
  /** "Just moved in" or "Settled" (journey.ts). Absent = not answered: the path asks. */
  stage?: import("../journey.ts").HomeStage;
  /** Guests often? (journey.ts: the guest-bathroom item). Absent = not answered. */
  guests?: boolean;
  /**
   * Children who take turns on the parent's phone (review C2): a first name only, never an account or a PIN. A chore a
   * child finishes is logged as the parent's account "done by" that name (home_chore_done.kid_id).
   */
  kids?: HomeKid[];
  /** The home's path (journey.ts PlaceJourney): done, put off, "What do you have?" answers. */
  journey?: import("../journey.ts").PlaceJourney;
  /** The home's own product and link per purpose (products.ts). Kept by PUT /homes/:orgId/products/:purpose only. */
  productOverrides?: import("../products.ts").ProductOverride[];
  /** Optional details that pick the maker's interval. Unknown → the app says "check your manual". */
  details?: {
    /** CRI vacuum schedule by traffic (home-cleaning-appliances F5). Default: high with pets, else light. */
    carpetTraffic?: "high" | "medium" | "light";
    /** Sleep Foundation: sheets every 3–4 days when pets sleep in the bed (home-laundry-bedroom L3). */
    petsInBed?: boolean;
    mattress?: "foam_latex" | "innerspring_new" | "innerspring_old" | "one_sided_maker" | "unknown";
    pitcherFilter?: "brita_standard" | "brita_elite" | "brita_stream" | "brita_faucet" | "other";
    roMaker?: "apec" | "ispring" | "other";
    water?: "city" | "well";
    espressoMaker?: "breville" | "delonghi" | "other";
    /** Utah garages freeze and bake (home-storage-organization, Differences 5): only a conditioned garage is offered. */
    garageConditioned?: boolean;
    /** A basement that stays dry (Differences 7): the deciding factor for food, paint and gear. */
    basementDry?: boolean;
    /** IANA time zone for "today"; default America/Denver. */
    timezone?: string;
    /** Children visit (grandkids, friends' kids): chemicals and medicines tighten like a home with children (spots.ts). Absent = not asked. */
    kidsVisit?: boolean;
    /** The places the household named for storing things (spots.ts). Absent = never asked; [] = asked, none named. */
    spots?: HomeSpot[];
  };
}

/* ── Storage spots (homespots.ts): the places the household named ── */
export type SpotArea =
  | "kitchen_sink" | "laundry" | "garage" | "high_cabinet" | "hall_closet" | "pantry" | "upper_cabinet" | "lower_cabinet"
  | "bathroom" | "basement" | "medicine_spot" | "other";
export const SPOT_AREAS: readonly SpotArea[] = ["kitchen_sink", "laundry", "garage", "high_cabinet", "hall_closet", "pantry", "upper_cabinet", "lower_cabinet", "bathroom", "basement", "medicine_spot", "other"];
export interface HomeSpot {
  id: string;
  area: SpotArea;
  /** Above the shoulders, out of a child's reach and sight. Only true when the household said so. */
  high: boolean;
  /** Behind a lock or a child-resistant latch. */
  locked: boolean;
  /** Their own words ("top shelf"), up to 40 characters. */
  name?: string;
}
export const MAX_SPOTS = 12;

export function validateSpots(v: unknown): string[] {
  if (!Array.isArray(v)) return ['"details.spots" must be a list.'];
  if (v.length > MAX_SPOTS) return [`"details.spots" can hold up to ${MAX_SPOTS} spots.`];
  const out: string[] = [];
  for (const s of v as Record<string, unknown>[]) {
    if (typeof s !== "object" || s === null) { out.push('Each spot must be an object.'); continue; }
    if (typeof s.id !== "string" || !/^[a-z0-9_-]{1,40}$/.test(s.id)) out.push('A spot "id" must be 1 to 40 letters, digits, - or _.');
    if (!SPOT_AREAS.includes(s.area as SpotArea)) out.push(`A spot "area" must be one of ${SPOT_AREAS.join(", ")}.`);
    if (typeof s.high !== "boolean" || typeof s.locked !== "boolean") out.push('A spot "high" and "locked" must be true or false.');
    if (s.name !== undefined && (typeof s.name !== "string" || s.name.length > 40)) out.push('A spot "name" must be up to 40 characters.');
  }
  return out;
}

export const HOME_TZ = "America/Denver";

export interface HomeKid { id: string; name: string }
export const MAX_KIDS = 12;
/** A kid id from a first name ("Ava" → "ava"), unique within the list. */
export function kidId(name: string, taken: readonly string[] = []): string {
  const base = name.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 30) || "kid";
  let id = base, n = 2;
  while (taken.includes(id)) id = `${base}-${n++}`;
  return id;
}

/** A starting profile: one bedroom, one full bath, a small kitchen, a glass-ceramic stove, no appliances. */
export function defaultHomeProfile(): HomeProfile {
  return {
    bedrooms: 1, bathrooms: { full: 1, half: 0 }, kitchenSize: "small", cabinets: ["upper", "lower", "under_sink"], stove: "glass_ceramic",
    appliances: [], floors: ["vinyl"], household: { adults: 1, kids: 0, pets: 0 },
  };
}

export const hasAppliance = (p: HomeProfile, a: HomeAppliance) => p.appliances.includes(a);
export const hasFloor = (p: HomeProfile, f: FloorType) => p.floors.includes(f);
export const isGlassStove = (p: HomeProfile) => p.stove === "induction_glass" || p.stove === "glass_ceramic";
export const hasKids = (p: HomeProfile) => p.household.kids > 0;
export const hasPets = (p: HomeProfile) => p.household.pets > 0;
export const bathroomCount = (p: HomeProfile) => p.bathrooms.full + p.bathrooms.half;

/** Checks a profile from the client. Returns a list of problems in plain words ([] = valid). */
export function validateHomeProfile(v: unknown): string[] {
  const out: string[] = [];
  if (typeof v !== "object" || v === null) return ["The profile must be an object."];
  const p = v as Record<string, unknown>;
  const int = (x: unknown, name: string, max: number) => { if (!Number.isInteger(x) || (x as number) < 0 || (x as number) > max) out.push(`"${name}" must be a whole number from 0 to ${max}.`); };
  const oneOf = <T extends string>(x: unknown, name: string, allowed: readonly T[]) => { if (!allowed.includes(x as T)) out.push(`"${name}" must be one of ${allowed.join(", ")}.`); };
  const listOf = <T extends string>(x: unknown, name: string, allowed: readonly T[]) => {
    if (!Array.isArray(x) || x.some((i) => !allowed.includes(i as T))) out.push(`"${name}" must be a list of ${allowed.join(", ")}.`);
  };
  int(p.bedrooms, "bedrooms", 20);
  const b = p.bathrooms as Record<string, unknown> | undefined;
  if (typeof b !== "object" || b === null) out.push('"bathrooms" must be { full, half }.');
  else { int(b.full, "bathrooms.full", 20); int(b.half, "bathrooms.half", 20); }
  oneOf(p.kitchenSize, "kitchenSize", KITCHEN_SIZES);
  listOf(p.cabinets, "cabinets", HOME_CABINETS);
  oneOf(p.stove, "stove", STOVE_TYPES);
  listOf(p.appliances, "appliances", HOME_APPLIANCES);
  listOf(p.floors, "floors", FLOOR_TYPES);
  const h = p.household as Record<string, unknown> | undefined;
  if (typeof h !== "object" || h === null) out.push('"household" must be { adults, kids, pets }.');
  else { int(h.adults, "household.adults", 30); int(h.kids, "household.kids", 30); int(h.pets, "household.pets", 30); }
  if (p.stage !== undefined && p.stage !== "just_moved" && p.stage !== "settled") out.push('"stage" must be just_moved or settled.');
  if (p.guests !== undefined && typeof p.guests !== "boolean") out.push('"guests" must be true or false.');
  if (p.kids !== undefined) {
    const k = p.kids as unknown[];
    if (!Array.isArray(k) || k.length > MAX_KIDS || !k.every((x) => typeof x === "object" && x !== null && typeof (x as HomeKid).id === "string" && /^[a-z0-9-]{1,40}$/.test((x as HomeKid).id) && typeof (x as HomeKid).name === "string" && (x as HomeKid).name.trim().length >= 1 && (x as HomeKid).name.length <= 40)) out.push(`"kids" must be up to ${MAX_KIDS} { id, name } (a first name).`);
    else if (new Set(k.map((x) => (x as HomeKid).id)).size !== k.length) out.push('"kids" ids must be different.');
  }
  if (p.journey !== undefined && (typeof p.journey !== "object" || p.journey === null || Array.isArray(p.journey))) out.push('"journey" must be an object.');
  if (p.details !== undefined) {
    const d = p.details as Record<string, unknown>;
    if (typeof d !== "object" || d === null) out.push('"details" must be an object.');
    else {
      const opt = <T extends string>(k: string, allowed: readonly T[]) => { if (d[k] !== undefined) oneOf(d[k], `details.${k}`, allowed); };
      opt("carpetTraffic", ["high", "medium", "light"] as const);
      opt("mattress", ["foam_latex", "innerspring_new", "innerspring_old", "one_sided_maker", "unknown"] as const);
      opt("pitcherFilter", ["brita_standard", "brita_elite", "brita_stream", "brita_faucet", "other"] as const);
      opt("roMaker", ["apec", "ispring", "other"] as const);
      opt("water", ["city", "well"] as const);
      opt("espressoMaker", ["breville", "delonghi", "other"] as const);
      if (d.spots !== undefined) out.push(...validateSpots(d.spots));
      for (const k of ["petsInBed", "garageConditioned", "basementDry", "kidsVisit"]) if (d[k] !== undefined && typeof d[k] !== "boolean") out.push(`"details.${k}" must be true or false.`);
      if (d.timezone !== undefined && (typeof d.timezone !== "string" || d.timezone.length > 60)) out.push('"details.timezone" must be a time zone name.');
    }
  }
  return out;
}
